import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

export const Section = {
	Name: 'name',
	Ids: 'ids',
	Contact: 'contact',
	Affiliations: 'affiliations',
	Addresses: 'addresses',
	Licenses: 'licenses'
} as const;

export const HcoSections = {
	...Section,
	Hierarchy: 'hierarchy',
	Organizational: 'organizational',
	Partners: 'partners'
} as const;

export type HcoSection = (typeof HcoSections)[keyof typeof HcoSections];

export const useHcoFieldNames = () => {
	const { t } = useTranslation();

	const hcoSections: Record<HcoSection, string> = useMemo(
		() => ({
			[HcoSections.Name]: t('Name'),
			[HcoSections.Organizational]: t('Organizational'),
			[HcoSections.Ids]: t('IDs'),
			[HcoSections.Contact]: t('Contact'),
			[HcoSections.Hierarchy]: t('Hierarchy'),
			[HcoSections.Affiliations]: t('Affiliation'),
			[HcoSections.Partners]: t('Partners'),
			[HcoSections.Addresses]: t('Address'),
			[HcoSections.Licenses]: t('License')
		}),
		[t]
	);

	const hcoSectionMap = useMemo(
		() => ({
			// Name
			hcoNameCda_V: HcoSections.Name,
			internationalNameCda_V: HcoSections.Name,

			// Organizational
			hcoTypeCda_V: HcoSections.Organizational,
			hcoTypeLocal_V: HcoSections.Organizational,

			allSpecLocal_V: HcoSections.Organizational,
			// spec1Local_V, spec2Local_V and specialty{n}_V are kept for already submitted DCRs
			spec1Local_V: HcoSections.Organizational,
			spec2Local_V: HcoSections.Organizational,
			specialty1_V: HcoSections.Organizational,
			specialty2_V: HcoSections.Organizational,
			specialty3_V: HcoSections.Organizational,
			specialty4_V: HcoSections.Organizational,
			specialty5_V: HcoSections.Organizational,
			specialty6_V: HcoSections.Organizational,
			specialty7_V: HcoSections.Organizational,
			specialty8_V: HcoSections.Organizational,
			specialty9_V: HcoSections.Organizational,
			specialty10_V: HcoSections.Organizational,
			primaryCountry_V: HcoSections.Organizational,
			academicStatus_V: HcoSections.Organizational,
			hcoOwnership_V: HcoSections.Organizational,
			businessType_V: HcoSections.Organizational,

			countHcp_V: HcoSections.Organizational,
			countPrescriber_V: HcoSections.Organizational,
			vaDodAffiliated_V: HcoSections.Organizational,
			nhidCda_V: HcoSections.Ids,
			hcoTaxId_V: HcoSections.Ids,
			statusCda_V: HcoSections.Organizational,

			// Contact
			officePhoneCda_V: HcoSections.Contact,
			phoneCda_V: HcoSections.Contact,
			faxCda_V: HcoSections.Contact,
			emailCda_V: HcoSections.Contact,
			websiteCda_V: HcoSections.Contact,
			URL1_V: HcoSections.Contact,

			// Hierarchy
			typeOfHierarchy_V: HcoSections.Hierarchy,

			// Affiliations // Note (Ben): Shouldn't be needed as not 'root' group
			parentHcos_V: HcoSections.Affiliations,
			parentHcoVid_V: HcoSections.Affiliations,
			parentHcoStatus_V: HcoSections.Affiliations,
			isPrimaryRelationship_V: HcoSections.Affiliations,
			// hcoTypeCda_V: HcoSections.Affiliations,
			locationRole_V: HcoSections.Affiliations,
			jobTitle_V: HcoSections.Affiliations,
			role_V: HcoSections.Affiliations,
			typeOfRelationship_V: HcoSections.Affiliations,
			relationshipType_V: HcoSections.Affiliations,
			principalAffiliationName_V: HcoSections.Affiliations,

			// Addresses // Note (Ben): Shouldn't be needed as not 'root' group
			addresses_V: HcoSections.Addresses,
			primaryCda_V: HcoSections.Addresses,
			streetAddress1Cda_V: HcoSections.Addresses,
			streetAddress2Cda_V: HcoSections.Addresses,
			cityCda_V: HcoSections.Addresses,
			stateCda_V: HcoSections.Addresses,
			postalCodeCda_V: HcoSections.Addresses,
			postalCodeFullUs_V: HcoSections.Addresses,
			countryCda_V: HcoSections.Addresses,
			coordinates_V: HcoSections.Addresses,
			subStateAdminUnit_V: HcoSections.Addresses,
			subAdministrativeArea_V: HcoSections.Addresses,
			cbsa_V: HcoSections.Addresses,
			phone1_V: HcoSections.Addresses,
			phone2_V: HcoSections.Addresses,
			fax1_V: HcoSections.Addresses,

			// Licenses // Note (Ben): Shouldn't be needed as not 'root' group
			licenseNumber_V: HcoSections.Licenses,
			licenseDegree_V: HcoSections.Licenses,
			typeValue_V: HcoSections.Licenses,
			expirationDate_V: HcoSections.Licenses,
			gracePeriod_V: HcoSections.Licenses,
			rxaEligible_V: HcoSections.Licenses,
			type_V: HcoSections.Licenses,
			licenseSubtype_V: HcoSections.Licenses,
			body_V: HcoSections.Licenses,
			licenseCertification_V: HcoSections.Licenses,
			bestStateLicense_V: HcoSections.Licenses,
			licenseStatus_V: HcoSections.Licenses,
			licenseEligibility_V: HcoSections.Licenses,
			licenseStatusCondition_V: HcoSections.Licenses,
			comprehensivePrescriptiveAuthority_V: HcoSections.Licenses,
			comprehensiveSampleEligibility_V: HcoSections.Licenses,
			collabAgreeRequired_V: HcoSections.Licenses,
			ctrlSubstancePrescriptive_V: HcoSections.Licenses,
			ctrlSubstanceSample_V: HcoSections.Licenses,
			deaBusinessActivityCode_V: HcoSections.Licenses,
			deaPaymentIndicator_V: HcoSections.Licenses,
			drugSchedule_V: HcoSections.Licenses
		}),
		[]
	);

	const hcoFieldNames = useMemo(
		() => ({
			// Organizational
			hcoNameCda_V: t('Name'),
			internationalNameCda_V: t('International Name'),
			hcoTypeCda_V: t('Global HCO Type'),

			primaryCountry_V: t('Country'),

			allSpecLocal_V: t('Local Specialties'),
			spec1Local_V: t('Primary Local Specialty'),
			spec2Local_V: t('Local Specialty 2'),
			specialty1_V: t('Primary Local Specialty'),
			specialty2_V: t('Local Specialty 2'),
			specialty3_V: t('Local Specialty 3'),
			specialty4_V: t('Local Specialty 4'),
			specialty5_V: t('Local Specialty 5'),
			specialty6_V: t('Local Specialty 6'),
			specialty7_V: t('Local Specialty 7'),
			specialty8_V: t('Local Specialty 8'),
			specialty9_V: t('Local Specialty 9'),
			specialty10_V: t('Local Specialty 10'),

			statusCda_V: t('Status'),
			nhidCda_V: t('National Healthcare ID'),
			businessType_V: t('Ownership'),
			hcoTaxId_V: t('Tax ID'),

			hcoOwnership_V: t('Ownership'),
			academicStatus_V: t('Academic Status'),
			countPrescriber_V: t('Number of Affiliated Prescribers'),
			countHcp_V: t('Number of Total Affiliated HCPs'),
			typeOfHierarchy_V: t('Type of Hierarchy'),
			hcoTypeLocal_V: t('Local HCO Type'),

			// Contact
			officePhoneCda_V: t('Office Phone'),
			phoneCda_V: t('Phone'),
			faxCda_V: t('Fax'),
			emailCda_V: t('Email'),
			websiteCda_V: t('Website'),
			URL1_V: t('Website'),

			// Affiliations
			parentHcos_V: t('Affiliation'),
			parentHcoVid_V: t('ID'),
			parentHcoStatus_V: t('Status'),
			isPrimaryRelationship_V: t('Is Primary'),
			locationRole_V: t('Location'),
			jobTitle_V: t('Job Title'),
			role_V: t('Job Title'),
			typeOfRelationship_V: t('Type of Relationship'),
			relationshipType_V: t('Type of Relationship'),
			principalAffiliationName_V: t('Principle Name'),

			// Addresses
			addresses_V: t('Address'),
			primaryCda_V: t('Is Primary'),
			streetAddress1Cda_V: t('Street Address 1'),
			streetAddress2Cda_V: t('Street Address 2'),
			cityCda_V: t('City'),
			stateCda_V: t('State'),
			postalCodeCda_V: t('Postal Code'),
			postalCodeFullUs_V: t('Full Postal Code US'),
			countryCda_V: t('Country'),
			coordinates_V: t('Coordinates'),
			subStateAdminUnit_V: t('Region/County'),
			subAdministrativeArea_V: t('Region/County'),
			cbsa_V: t('Metropolitan (CBSA)'),
			phone1_V: t('Phone 1'),
			phone2_V: t('Phone 2'),
			fax1_V: t('Fax'),

			// Licenses
			licenseNumber_V: t('License ID'),
			licenseDegree_V: t('License Degree'),
			typeValue_V: t('Licensing Authority'),
			expirationDate_V: t('Expiration Date'),
			gracePeriod_V: t('Grace Expiry'),
			rxaEligible_V: t('Sample Eligibility'),
			type_V: t('License Type'),
			licenseSubtype_V: t('License SubType'),
			body_V: t('Licensing Body'),
			licenseCertification_V: t('License Certification'),
			bestStateLicense_V: t('Best State License'),
			licenseStatus_V: t('License Status'),
			licenseEligibility_V: t('Prescriptive Authority'),
			licenseStatusCondition_V: t('License Status Condition'),
			comprehensivePrescriptiveAuthority_V: t(
				'Comprehensive Prescriptive Authority'
			),
			comprehensiveSampleEligibility_V: t('Comprehensive Sample Eligibility'),
			collabAgreeRequired_V: t('Collaborative Agreement Required'),
			ctrlSubstancePrescriptive_V: t(
				'Controlled Substances Prescriptive Authority'
			),
			ctrlSubstanceSample_V: t('Controlled Substances Sample Eligibility'),
			deaBusinessActivityCode_V: t('DEA Activity Code'),
			deaPaymentIndicator_V: t('DEA Payment Indicator'),
			drugSchedule_V: t('Drug Schedule Details'),

			// Local Attributes
			majorClassOfTrade_V: t('Major Class of Trade'),
			vaDodAffiliated_V: t('Veterans Affairs - Department of Defense')
		}),
		[t]
	);

	return {
		hcoFieldNames,
		hcoSectionMap,
		hcoSections
	};
};
